import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { getCompetitorReports } from '../../api_handlers/competitor';
import {
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  Box,
  Chip,
  Paper,
} from '@material-ui/core';
import { Assessment, ArrowBack, Add, GetApp, Visibility, Instagram, YouTube, Description } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  heroSection: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  iconContainer: {
    width: 64,
    height: 64,
    borderRadius: theme.shape.borderRadius,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
    marginBottom: theme.spacing(2),
  },
  icon: {
    color: 'white',
    fontSize: 32,
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  reportCard: {
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    '&:hover': {
      transform: 'translateY(-4px) scale(1.02)',
      boxShadow: theme.shadows[12],
    },
  },
  reportCardHeader: {
    paddingBottom: theme.spacing(1),
  },
  reportCardContent: {
    paddingTop: theme.spacing(1),
  },
  metricGrid: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  metricItem: {
    padding: theme.spacing(1),
  },
  metricLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  metricValue: {
    fontSize: '1.125rem',
    fontWeight: 700,
  },
  badgeContainer: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  actionButtons: {
    marginTop: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(1),
  },
  typeBadge: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  platformBadge: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));

export default function CompetitorReport() {
  const classes = useStyles();
  const history = useHistory();
  const [view, setView] = useState('landing'); // landing, detail
  const [selectedReport, setSelectedReport] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCompetitorReports();
      
      if (response && response.results) {
        // Group reports by brand_name - each brand group becomes one card
        const groupedReports = response.results.map((brandGroup) => {
          const reports = brandGroup.reports || [];
          const brandName = brandGroup.brand_name || reports[0]?.brand_name || 'Unknown Brand';
          const brandId = brandGroup.brand_id || reports[0]?.brand_id;
          
          // Find report with lowest sequence number
          const sortedBySequence = [...reports].sort((a, b) => {
            const seqA = a.sequence !== undefined ? a.sequence : Infinity;
            const seqB = b.sequence !== undefined ? b.sequence : Infinity;
            return seqA - seqB;
          });
          const lowestSequenceReport = sortedBySequence[0];
          const cardTitle = lowestSequenceReport?.title || brandName;
          
          // Get the most recent date from all reports
          const dates = reports
            .map((r) => new Date(r.created_at || r.updated_at || 0))
            .filter((d) => !isNaN(d.getTime()));
          const latestDate = dates.length > 0 ? new Date(Math.max(...dates)) : null;
          
          // Collect all competitors from all reports
          const competitors = reports
            .map((report) => ({
              title: report.title || report.ig_handle || 'Untitled',
              ig_handle: report.ig_handle && report.ig_handle !== 'Test - blank YT' ? report.ig_handle : null,
              ig_label:
                report.ig_source_type === 'hashtag'
                  ? `#${report.ig_hashtag}`
                  : report.ig_source_type === 'handle_hashtag'
                    ? `${report.ig_account_handle} + #${report.ig_hashtag}`
                    : report.ig_handle,
              yt_handle: report.yt_handle && report.yt_handle !== 'Test - blank YT' ? report.yt_handle : null,
              created_at: report.created_at,
              updated_at: report.updated_at,
            }))
            .filter((comp) => comp.ig_handle || comp.yt_handle);
          
          // Determine platform (if any report has both, show Both)
          const hasIG = reports.some((r) => r.ig_handle && r.ig_handle !== 'Test - blank YT');
          const hasYT = reports.some((r) => r.yt_handle && r.yt_handle !== 'Test - blank YT');
          const platform = hasIG && hasYT ? 'Both' : hasIG ? 'Instagram' : 'YouTube';
          
          return {
            id: brandId || `brand-${brandName}`,
            brandName,
            brandId,
            title: cardTitle, // Title from report with lowest sequence
            dateGenerated: latestDate ? latestDate.toISOString() : null,
            dateRange: 'N/A', // Could be calculated from report dates if needed
            platform,
            type: 'Pre-generated',
            competitors, // Array of competitor objects
            reportCount: reports.length,
            reports, // Keep original reports for reference
          };
        });
        
        // Sort by latest date first (most recent first)
        const sortedReports = groupedReports.sort((a, b) => {
          const dateA = new Date(a.dateGenerated || 0);
          const dateB = new Date(b.dateGenerated || 0);
          return dateB - dateA; // Descending order (newest first)
        });
        
        setReports(sortedReports);
      } else {
        setReports([]);
      }
    } catch (err) {
      console.error('Error loading reports:', err);
      setError('Failed to load reports. Please try again.');
      setReports([]);
    } finally {
      setLoading(false);
    }
  };


  const handleViewReport = (report) => {
    setSelectedReport(report);
    setView('detail');
  };

  const handleExportReport = (report) => {
    alert(`Exporting report: ${report.title}`);
  };

  const handleBackToLanding = () => {
    setView('landing');
    setSelectedReport(null);
  };

  // Landing View
  if (view === 'landing') {
    return (
      <div className={classes.root}>
        <Box className={classes.heroSection}>
          <div className={classes.iconContainer}>
            <Assessment className={classes.icon} />
          </div>
          <Typography variant="h3" component="h1" gutterBottom>
            Competitor Report
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            View sample reports or create your own custom competitor analysis
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<Add />}
            onClick={() => history.push('/competitor-report/create')}
            style={{ marginTop: 16 }}
          >
            Create New Report
          </Button>
        </Box>

        <Typography variant="h5" gutterBottom style={{ marginTop: 32 }}>
          {loading ? 'Loading Reports...' : reports.length > 0 ? 'Competitor Reports' : 'No Reports'}
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          {loading
            ? 'Please wait while we load your reports...'
            : reports.length > 0
            ? 'View and manage your competitor analysis reports'
            : 'Create your first competitor report to get started'}
        </Typography>

        {error && (
          <Box marginBottom={2} padding={2} style={{ backgroundColor: '#fee2e2', borderRadius: 4 }}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" padding={4}>
            <Typography>Loading...</Typography>
          </Box>
        ) : reports.length === 0 ? (
          <Box textAlign="center" padding={4}>
            <Typography variant="body1" color="textSecondary" paragraph>
              No reports found. Create your first competitor report to get started.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {reports.map((report) => {
            // Calculate metrics if available (placeholder for now)
            const PlatformIcon = report.platform === 'Instagram' ? Instagram : report.platform === 'YouTube' ? YouTube : Description;

            return (
              <Grid item xs={12} md={6} lg={4} key={report.id}>
                <Card
                  className={`${classes.card} ${classes.reportCard}`}
                  onClick={() => handleViewReport(report)}
                >
                  <CardHeader
                    className={classes.reportCardHeader}
                    avatar={
                      <Box
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 8,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Description style={{ color: 'white', fontSize: 20 }} />
                      </Box>
                    }
                    title={
                      <Typography variant="h6" style={{ fontSize: '1.125rem', fontWeight: 600 }}>
                        {report.title || report.brandName}
                      </Typography>
                    }
                    subheader={
                      <Typography variant="caption" color="textSecondary">
                        {report.brandName}
                      </Typography>
                    }
                  />
                  <CardContent className={classes.reportCardContent}>
                    {/* Badges */}
                    <Box className={classes.badgeContainer}>
                      <Chip
                        label={report.type}
                        size="small"
                        className={classes.typeBadge}
                        style={{
                          backgroundColor:
                            report.type === 'Pre-generated'
                              ? '#dbeafe'
                              : report.type === 'Template'
                              ? '#d1fae5'
                              : '#e9d5ff',
                          color:
                            report.type === 'Pre-generated'
                              ? '#1e40af'
                              : report.type === 'Template'
                              ? '#065f46'
                              : '#6b21a8',
                        }}
                      />
                      <Chip
                        icon={<PlatformIcon style={{ fontSize: 14 }} />}
                        label={report.platform}
                        size="small"
                        variant="outlined"
                        className={classes.platformBadge}
                      />
                    </Box>

                    {/* Report Info */}
                    <Box marginTop={2} marginBottom={2}>
                      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                        Brand: {report.brandName}
                      </Typography>
                      {report.reportCount > 0 && (
                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                          {report.reportCount} {report.reportCount === 1 ? 'Competitor' : 'Competitors'} Analyzed
                        </Typography>
                      )}
                      <Typography variant="body2" color="textSecondary">
                        Date Range: {report.dateRange}
                      </Typography>
                    </Box>

                    {/* Competitors */}
                    <Box marginTop={2} marginBottom={2}>
                      <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 8 }}>
                        Competitors Analyzed
                      </Typography>
                      <Box display="flex" flexWrap="wrap">
                        {report.competitors && report.competitors.length > 0 ? (
                          report.competitors.map((competitor, idx) => (
                            <React.Fragment key={idx}>
                              {competitor.ig_handle && (
                                <Chip
                                  label={`IG: ${competitor.ig_label}`}
                                  size="small"
                                  variant="outlined"
                                  style={{ marginRight: 4, marginBottom: 4 }}
                                />
                              )}
                              {competitor.yt_handle && (
                                <Chip
                                  label={`YT: ${competitor.yt_handle}`}
                                  size="small"
                                  variant="outlined"
                                  style={{ marginRight: 4, marginBottom: 4 }}
                                />
                              )}
                            </React.Fragment>
                          ))
                        ) : (
                          <Typography variant="caption" color="textSecondary">
                            No competitors specified
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Date */}
                    {report.dateGenerated && (
                      <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 16 }}>
                        Created on{' '}
                        {new Date(report.dateGenerated).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </Typography>
                    )}

                    {/* Action Buttons */}
                    <Box className={classes.actionButtons}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<Visibility />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewReport(report);
                        }}
                        style={{ flex: 1 }}
                      >
                        View Details
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<GetApp />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExportReport(report);
                        }}
                      >
                        Export
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        )}
      </div>
    );
  }


  // Detail View
  return (
    <div className={classes.root}>
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={3}>
        <Box>
          <Typography variant="h4" component="h1">
            {selectedReport ? selectedReport.title : 'Competitor Report'}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {selectedReport ? selectedReport.description : 'Detailed competitor analysis'}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<GetApp />}
          onClick={() => handleExportReport(selectedReport)}
        >
          Export
        </Button>
      </Box>

      <Paper style={{ padding: 24 }}>
        <Typography variant="h6" gutterBottom>
          Report Details
        </Typography>
        <Typography variant="body1" paragraph>
          This is a placeholder for the detailed report view. The full implementation
          would include charts, analytics, and detailed comparisons similar to the
          influencer-dashboard version.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Date Range: {selectedReport?.dateRange}
        </Typography>
        <Box marginTop={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={handleBackToLanding}
          >
            Back to Reports
          </Button>
        </Box>
      </Paper>
    </div>
  );
}

